import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
   IPlaybook,
   ITranscriptLine,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import {
   ITimelineItem,
   sortTouches,
   spokenLines,
   textFor,
   timeline,
   voicemailFor,
} from '@forwardflow/features/campaign-flow/utils/playbook'

/**
 * "Try it as a lead": the user plays the lead.
 *
 * A call rings and waits: Accept starts the conversation (the caller says the
 * first point of the script, then waits for the user's reply before the next),
 * Decline leaves the voicemail if that call has one. A text arrives and waits
 * for a reply. Nothing moves on by itself except the caller's answers.
 * Front-end simulation; the live build can swap the call for a real voice
 * session (Campaign Studio uses ElevenLabs ConvAI / Gemini Live).
 */

export type TRunPhase =
   'idle' | 'ringing' | 'live' | 'ended' | 'missed' | 'text'

export interface IThreadMessage {
   id: string
   from: 'assistant' | 'lead'
   text: string
   /** Index into playbook.texts when the message is a playbook text. */
   textIndex?: number
}

const ANSWER_MS = 900

export const QUICK_REPLIES = [
   'Sure, go ahead',
   'Who is this?',
   'Call me later',
   'Not interested',
]

export const useTestRun = (
   playbook: IPlaybook,
   fill: (text: string) => string
) => {
   /* Voicemails belong to their call: they play when the user declines it. */
   const items = useMemo(
      () => timeline(playbook).filter((i) => i.kind !== 'voicemail'),
      [playbook]
   )
   const [cursor, setCursor] = useState(-1)
   const [phase, setPhase] = useState<TRunPhase>('idle')
   const [beat, setBeat] = useState(0)
   const [lines, setLines] = useState<ITranscriptLine[]>([])
   const [thread, setThread] = useState<IThreadMessage[]>([])
   const [thinking, setThinking] = useState(false)
   const timers = useRef<number[]>([])
   const clear = () => {
      timers.current.forEach((t) => window.clearTimeout(t))
      timers.current = []
      setThinking(false)
   }
   const later = (fn: () => void, ms = ANSWER_MS) => {
      setThinking(true)
      timers.current.push(
         window.setTimeout(() => {
            setThinking(false)
            fn()
         }, ms)
      )
   }
   useEffect(
      () => () => timers.current.forEach((t) => window.clearTimeout(t)),
      []
   )

   const item: ITimelineItem | null =
      cursor >= 0 ? (items[cursor] ?? null) : null

   /** The voicemail index for a call item, or -1 when that call leaves none. */
   const voicemailOf = useCallback(
      (it: ITimelineItem | null) => {
         if (!it || it.kind !== 'call') return -1
         const calls = sortTouches(playbook.touches).filter(
            (t) => t.action === 'call'
         )
         const call = calls.find((t) => t.id === it.key)
         if (!call?.voicemail) return -1
         return calls.filter((t) => t.voicemail).indexOf(call)
      },
      [playbook.touches]
   )

   const enter = (index: number, missed = false) => {
      clear()
      const next = items[index]
      setCursor(next ? index : -1)
      setLines([])
      setBeat(0)
      if (!next) return setPhase('idle')
      if (next.kind === 'call') return setPhase(missed ? 'missed' : 'ringing')
      /* A text: everything sent so far is in the thread, the new one at the bottom. */
      setPhase('text')
      setThread((t) => {
         const earlier = items
            .slice(0, index + 1)
            .filter((i) => i.kind === 'text')
         const kept = t.filter(
            (m) =>
               m.textIndex === undefined ||
               earlier.some((e) => e.index === m.textIndex)
         )
         const have = new Set(
            kept
               .filter((m) => m.textIndex !== undefined)
               .map((m) => m.textIndex)
         )
         return [
            ...kept,
            ...earlier
               .filter((e) => !have.has(e.index))
               .map((e) => ({
                  id: e.key,
                  from: 'assistant' as const,
                  text: '',
                  textIndex: e.index,
               })),
         ]
      })
   }

   const start = () => {
      setThread([])
      enter(0)
   }
   const next = () =>
      cursor + 1 < items.length
         ? enter(cursor + 1)
         : (clear(), setCursor(-1), setPhase('idle'))
   const reset = () => {
      clear()
      setCursor(-1)
      setPhase('idle')
      setLines([])
      setThread([])
   }
   /** Jump to a touch. A voicemail key ("<callId>-vm") opens its call as missed. */
   const startAt = (key: string) => {
      const vm = key.endsWith('-vm')
      const i = items.findIndex(
         (it) => it.key === (vm ? key.slice(0, -3) : key)
      )
      if (i >= 0) enter(i, vm)
   }

   /* ---- calls ---- */
   const accept = () => {
      if (phase !== 'ringing') return
      setPhase('live')
      /* The opening line is read from the playbook when shown, so an edit changes it on the phone at once. */
      setLines([{ from: 'assistant', text: '', ref: 'opening' }])
      setBeat(1)
   }
   const decline = () => phase === 'ringing' && setPhase('missed')
   const hangUp = () => {
      clear()
      setPhase('ended')
   }
   const say = (text: string) => {
      if (phase !== 'live' || !text.trim()) return
      setLines((l) => [...l, { from: 'lead', text }])
      const s = text.toLowerCase()
      const end = (line: string) =>
         later(() => {
            setLines((l) => [...l, { from: 'assistant', text: line }])
            setPhase('ended')
         })
      if (/not interested|stop|remove|don't call/.test(s))
         return end("No problem at all. I won't call again. Have a great day.")
      if (/later|busy|call back|not now/.test(s))
         return end(
            "Of course. I'll text you so you have my number. Talk soon."
         )
      if (/who is|who's this|what is this|what company/.test(s))
         return later(() =>
            setLines((l) => [
               ...l,
               {
                  from: 'assistant',
                  text: fill(
                     'This is {assistant name}. You were looking at homes near {city}.'
                  ),
               },
            ])
         )
      const spoken = spokenLines(playbook)
      if (beat >= spoken.length)
         return end(
            'Perfect. I just sent you a text with the details. Talk soon!'
         )
      const line = fill(spoken[beat])
      later(() => {
         setLines((l) => [...l, { from: 'assistant', text: line }])
         setBeat((b) => b + 1)
      })
   }

   /* ---- texts ---- */
   const reply = (text: string) => {
      if (!text.trim()) return
      setThread((t) => [...t, { id: `l${Date.now()}`, from: 'lead', text }])
      const s = text.toLowerCase()
      const answer = /who|what.*(company|this)/.test(s)
         ? fill(
              'This is {assistant name}. You were looking at homes near {city}.'
           )
         : /listing|homes|send|house/.test(s)
           ? 'I will send the three best matches. Any must-haves, like bedrooms or a yard?'
           : /stop|not interested/.test(s)
             ? "Understood, you won't hear from us again."
             : /call/.test(s)
               ? 'Calling you now.'
               : 'Got it. Would Tuesday 2pm or Wednesday 11am work for a quick chat?'
      later(
         () =>
            setThread((t) => [
               ...t,
               { id: `a${Date.now()}`, from: 'assistant', text: answer },
            ]),
         1100
      )
   }

   const nextItem = cursor + 1 < items.length ? items[cursor + 1] : null
   const vmIndex = voicemailOf(item)

   /* Lines taken from the playbook show its current words. */
   const shownLines = lines.map((l) =>
      l.ref === 'opening'
         ? { ...l, text: fill(spokenLines(playbook)[0] ?? '') }
         : l
   )

   return {
      items,
      item,
      cursor,
      phase,
      lines: shownLines,
      thinking,
      /* Playbook texts always show their current words, so edits appear on the phone at once. */
      thread: thread.map((m) =>
         m.textIndex !== undefined && textFor(playbook, m.textIndex)
            ? { ...m, text: fill(textFor(playbook, m.textIndex).text) }
            : m
      ),
      voicemail:
         vmIndex >= 0
            ? fill(voicemailFor(playbook, vmIndex)?.text ?? '')
            : null,
      quickReplies: QUICK_REPLIES,
      start,
      next,
      reset,
      startAt,
      accept,
      decline,
      hangUp,
      say,
      reply,
      nextItem,
   }
}
